import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, User, Calendar, Check, AlertTriangle, Trophy } from "lucide-react";

const PILL = (color) => {
  switch (color) {
    case "red": case "orange": case "amber": return { bg: "bg-warn-soft", text: "text-warn", border: "border-warn/40" };
    case "light_green": case "dark_green": return { bg: "bg-good-soft", text: "text-good", border: "border-good/40" };
    default: return { bg: "bg-secondary", text: "text-ink-muted", border: "border-ink-line" };
  }
};
const BAR = (c) => (c === "red" || c === "orange" || c === "amber") ? "bg-warn"
  : (c === "light_green" || c === "dark_green") ? "bg-good" : "bg-brand";

export default function TapiDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [schema, setSchema] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: d }, { data: sch }] = await Promise.all([
          api.get(`/tapi/submissions/${id}`),
          api.get(`/tapi/schema`),
        ]);
        setData(d); setSchema(sch);
      } catch (ex) {
        toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
      }
    })();
  }, [id]);

  if (!data || !schema) return <div className="text-sm text-ink-muted uppercase tracking-wider">Loading…</div>;
  const pill = PILL(data.tier_color);
  const lessons = data.lessons || { root_causes: [], pricing_misses: [] };

  return (
    <div className="space-y-6" data-testid="tapi-detail-page">
      <button onClick={() => nav(-1)} className="text-[12px] uppercase tracking-wider text-ink-muted hover:text-ink flex items-center gap-1">
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </button>

      <div className="border-b border-ink-line pb-6">
        <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">TAPI Retrospective</div>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-1">{data.snapshot_tender_name || data.tender?.title || "—"}</h1>
        <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-ink-muted">
          <span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> {data.user_name || data.user_email || "—"}</span>
          <span className="flex items-center gap-1.5 font-num"><Calendar className="w-3.5 h-3.5" /> {data.completed_at ? new Date(data.completed_at).toLocaleString() : "—"}</span>
          {data.result_position && <span className="chip text-[10px] bg-secondary text-ink border-ink-line">Result: {data.result_position}</span>}
        </div>
      </div>

      {/* Headline */}
      <div className={`card-flat p-7 border-l-4 ${pill.border}`}>
        <div className="flex items-baseline gap-2">
          <div className="font-num text-7xl font-bold leading-none">{data.total_score}</div>
          <div className="text-2xl font-num text-ink-muted">/ 100</div>
        </div>
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <span className={`px-3 py-1 text-sm font-display font-bold rounded-sm border ${pill.bg} ${pill.text} ${pill.border}`}>{data.tier_label || "—"}</span>
        </div>
      </div>

      {/* Tender Context snapshot */}
      <div className="card-flat p-6">
        <div className="stat-label">Tender Context (snapshot)</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
          <div><div className="text-[11px] uppercase tracking-wider text-ink-muted">Client</div><div className="mt-1">{data.snapshot_client || "—"}</div></div>
          <div><div className="text-[11px] uppercase tracking-wider text-ink-muted">Value</div><div className="mt-1 font-num">{data.snapshot_tender_value || "—"}</div></div>
          <div><div className="text-[11px] uppercase tracking-wider text-ink-muted">Deadline</div><div className="mt-1 font-num">{data.snapshot_submission_date || "—"}</div></div>
          <div><div className="text-[11px] uppercase tracking-wider text-ink-muted">SR No</div><div className="mt-1 font-num">{data.snapshot_sr_no != null ? `#${String(data.snapshot_sr_no).padStart(3, "0")}` : "—"}</div></div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
          {[
            { k: "bnbd", l: "BNBD" }, { k: "pcei", l: "PCEI" }, { k: "pcab", l: "PCAB" },
          ].map(({ k, l }) => {
            const score = data[`snapshot_${k}_score`];
            const tierLabel = data[`snapshot_${k}_tier_label`];
            const tierColor = data[`snapshot_${k}_tier_color`];
            const tPill = tierColor ? PILL(tierColor) : null;
            return (
              <div key={k} className="border border-ink-line p-3 rounded-sm">
                <div className="text-[11px] uppercase tracking-wider text-ink-muted">{l} (pre-bid)</div>
                <div className="flex items-baseline gap-2 mt-1">
                  <div className="font-num text-2xl font-bold">{score ?? "—"}</div>
                  {score != null && <div className="text-xs text-ink-muted font-num">/100</div>}
                </div>
                {tierLabel && tPill && <span className={`chip text-[10px] mt-2 ${tPill.bg} ${tPill.text} ${tPill.border}`}>{tierLabel}</span>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Dimensional breakdown */}
      <div className="card-flat p-6">
        <div className="stat-label">Dimensional breakdown</div>
        <div className="space-y-3 mt-4">
          {(data.dimension_breakdown || []).map((d) => {
            const fp = PILL(d.flag_color);
            return (
              <div key={d.id} className="space-y-1">
                <div className="flex items-baseline justify-between text-sm">
                  <span className="font-medium">{d.label}</span>
                  <span className="flex items-center gap-2">
                    <span className="font-num text-ink-muted">{d.score} / {d.max_score}</span>
                    <span className={`chip text-[10px] ${fp.bg} ${fp.text} ${fp.border}`}>{d.flag_label}</span>
                  </span>
                </div>
                <div className="h-2 bg-secondary rounded-sm overflow-hidden">
                  <div className={`h-full transition-all ${BAR(d.flag_color)}`} style={{ width: `${Math.min(100, d.pct)}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {(lessons.root_causes.length > 0 || lessons.pricing_misses.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {lessons.root_causes.length > 0 && (
            <div className="card-flat p-6">
              <div className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-warn" /><div className="stat-label">Root causes</div></div>
              <ul className="mt-3 space-y-1.5">{lessons.root_causes.map((c) => <li key={c} className="text-sm flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-warn" />{c}</li>)}</ul>
            </div>
          )}
          {lessons.pricing_misses.length > 0 && (
            <div className="card-flat p-6">
              <div className="flex items-center gap-2"><Trophy className="w-4 h-4 text-brand" /><div className="stat-label">Pricing misses</div></div>
              <ul className="mt-3 space-y-1.5">{lessons.pricing_misses.map((c) => <li key={c} className="text-sm flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-brand" />{c}</li>)}</ul>
            </div>
          )}
        </div>
      )}

      {/* All 10 answers */}
      <div className="card-flat" data-testid="tapi-detail-answers">
        <div className="px-6 py-4 border-b border-ink-line">
          <div className="stat-label">Question-by-question detail</div>
          <div className="font-display font-bold text-lg tracking-tight mt-1">All 10 answers</div>
        </div>
        <div className="divide-y divide-ink-line">
          {schema.questions.map((q) => {
            const sel = (data.answers?.[q.code]) || [];
            const dim = schema.dimensions.find((d) => d.id === q.dimension);
            return (
              <div key={q.code} className="px-6 py-4">
                <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted mb-1">
                  {dim?.label} · {q.code} · {q.type === "multi" ? "Multi-select" : "Single-select"}
                </div>
                <div className="text-sm font-medium mb-3">{q.label}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {q.options.map((o, i) => {
                    const isSel = sel.includes(i);
                    return (
                      <div key={i} className={`flex items-center justify-between gap-2 px-3 py-2 text-sm rounded-sm border ${isSel ? "border-brand bg-brand/5 text-ink" : "border-ink-line bg-white text-ink-muted"}`}>
                        <span className="flex items-center gap-2">
                          <span className={`w-4 h-4 ${q.type === "multi" ? "rounded-sm" : "rounded-full"} border flex items-center justify-center shrink-0 ${isSel ? "border-brand bg-brand text-white" : "border-ink-line"}`}>
                            {isSel && <Check className="w-3 h-3" />}
                          </span>
                          {o.label}
                        </span>
                        <span className={`font-num text-xs ${isSel ? "text-brand font-bold" : "text-ink-muted"}`}>{o.score}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
